import { FOCUS_RING, Pills } from '@amelio/ui-library';

import { readPositionName } from './targetPosition';
import { TIMELINE } from './timelineGeometry';

import type { TimelineCluster, TimelineLayout } from './timelineGeometry';
import type { CareerStep } from '@/types/career-growth';
import type { RefObject } from 'react';

import { useAmelioTranslations, useSvgId } from '@/hooks';

type Translate = ReturnType<typeof useAmelioTranslations>['t'];

/**
 * The card's secondary lines. 11px is a deliberate step below `text-xs`: a badge and a date have
 * to fit a `cardWidth` card without pushing its title onto a third line.
 */
const CARD_META_TEXT = 'text-[11px] leading-tight font-medium';

const CARD_SURFACE = 'flex flex-col rounded-lg bg-white px-3 py-2.5 shadow-md';

/** `2019–21`, `2023 – present`, `Aimed for 2028`: the chart speaks in years, not in full dates. */
const formatStepPeriod = (step: CareerStep, t: Translate): string => {
    // Only a target reaches the chart without a date: it is drawn all the same, and says so.
    if (!step.startDate) {
        return t('performanceProfile.growth.timeline.undated');
    }

    const startYear = step.startDate.slice(0, 4);

    if (step.kind === 'Target') {
        return t('performanceProfile.growth.timeline.targetDate', { year: startYear });
    }

    if (!step.endDate) {
        return step.kind === 'Current'
            ? `${startYear} – ${t('performanceProfile.growth.timeline.present')}`
            : startYear;
    }

    const endYear = step.endDate.slice(0, 4);

    return endYear === startYear ? startYear : `${startYear}–${endYear.slice(2)}`;
};

interface StepCardProps {
    step: CareerStep;
    t: Translate;
}

/** The card of a step that stands alone on the chart. */
const StepCard = ({ step, t }: StepCardProps) => {
    const isTarget = step.kind === 'Target';

    return (
        <div
            className={`${CARD_SURFACE} gap-0.5 ${
                isTarget ? 'border-growth-violet-soft border border-dashed' : ''
            }`}
        >
            {step.kind === 'Current' && (
                <span className={`${CARD_META_TEXT} text-growth-muted`}>
                    {t('performanceProfile.growth.timeline.currentBadge')}
                </span>
            )}
            {isTarget && (
                <span className={`${CARD_META_TEXT} text-growth-violet-soft`}>
                    {t('performanceProfile.growth.timeline.targetBadge')}
                </span>
            )}
            <span
                className={`text-xs leading-tight font-semibold break-words ${
                    isTarget ? 'text-growth-violet-muted' : 'text-growth-violet-deep'
                }`}
            >
                {readPositionName(step)}
            </span>
            <span className={`${CARD_META_TEXT} text-growth-muted`}>
                {formatStepPeriod(step, t)}
            </span>
        </div>
    );
};

interface ClusterCardProps {
    steps: CareerStep[];
    t: Translate;
}

/**
 * The card of several steps whose dots sit closer than one card width. The count pill leads the
 * card in the flow rather than floating over its corner: it stays readable and needs no offset.
 */
const ClusterCard = ({ steps, t }: ClusterCardProps) => (
    <div className={`${CARD_SURFACE} gap-1.5`}>
        <Pills
            variant={'violet'}
            size={'xs'}
        >
            {t('performanceProfile.growth.timeline.clusterCount', { count: steps.length })}
        </Pills>
        {steps.map((step) => (
            <div
                key={step.id}
                className={'flex flex-col'}
            >
                {step.kind === 'Current' && (
                    <span className={`${CARD_META_TEXT} text-growth-muted`}>
                        {t('performanceProfile.growth.timeline.currentBadge')}
                    </span>
                )}
                <span
                    className={
                        'text-growth-violet-deep text-xs leading-tight font-semibold break-words'
                    }
                >
                    {readPositionName(step)}
                </span>
                <span className={`${CARD_META_TEXT} text-growth-muted`}>
                    {formatStepPeriod(step, t)}
                </span>
            </div>
        ))}
    </div>
);

interface DynamicTimelineProps {
    layout: TimelineLayout;
    /** Owned by the section, which also drives the scroll arrows. */
    scrollRef: RefObject<HTMLDivElement | null>;
    onScroll: () => void;
    dataTestId: string;
}

/**
 * The career path chart: a violet line climbing through the steps, a dashed segment towards the
 * target, and one card per cluster. The SVG is drawn at its real pixel size -- no `viewBox`
 * scaling -- so the cards, positioned in pixels from the same layout, land exactly on their dot.
 */
export const DynamicTimeline = ({
    layout,
    scrollRef,
    onScroll,
    dataTestId,
}: DynamicTimelineProps) => {
    const { t } = useAmelioTranslations();
    const areaGradientId = useSvgId('timeline-area');

    const clusterKey = (cluster: TimelineCluster) => cluster.steps.map((step) => step.id).join('+');

    return (
        <div
            ref={scrollRef}
            onScroll={onScroll}
            // Focusable, so the keyboard scrolls the chart with the arrow keys even when the
            // section's arrow buttons are not shown.
            tabIndex={0}
            role={'region'}
            aria-label={t('performanceProfile.growth.timeline.title')}
            className={`${FOCUS_RING} scrollbar-hide overflow-x-auto overflow-y-hidden`}
            data-testid={`${dataTestId}-scroll`}
        >
            <div
                className={'relative'}
                style={{
                    width: layout.width,
                    height: TIMELINE.height,
                }}
            >
                <svg
                    width={layout.width}
                    height={TIMELINE.height}
                    className={'absolute left-0'}
                    style={{ top: 0 }}
                    role={'img'}
                    aria-label={t('performanceProfile.growth.timeline.chartAria', {
                        count: layout.points.length,
                    })}
                >
                    <defs>
                        <linearGradient
                            id={areaGradientId}
                            x1={'0'}
                            y1={'0'}
                            x2={'0'}
                            y2={'1'}
                        >
                            <stop
                                offset={'0'}
                                style={{ stopColor: 'var(--color-growth-violet)' }}
                                stopOpacity={'0.35'}
                            />
                            <stop
                                offset={'1'}
                                style={{ stopColor: 'var(--color-growth-violet)' }}
                                stopOpacity={'0'}
                            />
                        </linearGradient>
                    </defs>

                    {layout.gridLines.map((y) => (
                        <line
                            key={y}
                            x1={TIMELINE.paddingX}
                            y1={y}
                            x2={layout.width - TIMELINE.paddingX}
                            y2={y}
                            className={'stroke-growth-line'}
                            strokeWidth={1}
                        />
                    ))}

                    {layout.areaPath && (
                        <path
                            d={layout.areaPath}
                            fill={`url(#${areaGradientId})`}
                        />
                    )}
                    {layout.linePath && (
                        <path
                            d={layout.linePath}
                            className={'stroke-growth-violet'}
                            strokeWidth={2}
                            strokeLinecap={'round'}
                            fill={'none'}
                        />
                    )}
                    {layout.targetPath && (
                        <path
                            d={layout.targetPath}
                            className={'stroke-growth-violet-soft'}
                            strokeWidth={2}
                            strokeDasharray={'6 4'}
                            fill={'none'}
                        />
                    )}

                    {layout.points.map((point) => (
                        <circle
                            key={point.step.id}
                            cx={point.x}
                            cy={point.y}
                            r={TIMELINE.dotRadius}
                            className={
                                point.step.kind === 'Target'
                                    ? 'fill-growth-violet-soft'
                                    : 'fill-growth-violet'
                            }
                            stroke={'white'}
                            strokeWidth={3}
                        />
                    ))}

                    <g className={'fill-growth-muted text-xs font-medium'}>
                        {layout.yearTicks.map((year) => (
                            <text
                                key={year}
                                x={layout.xForYear(year)}
                                y={TIMELINE.plotBottom + TIMELINE.yearLabelOffset}
                                textAnchor={'middle'}
                            >
                                {year}
                            </text>
                        ))}
                    </g>
                </svg>

                {layout.clusters.map((cluster) => (
                    <div
                        key={clusterKey(cluster)}
                        className={'absolute -translate-x-1/2'}
                        style={{
                            left: cluster.x,
                            top: Math.max(0, cluster.y - TIMELINE.cardLift),
                            width: TIMELINE.cardWidth,
                        }}
                        data-testid={`${dataTestId}-card-${cluster.steps[0].id}`}
                    >
                        {cluster.steps.length === 1 ? (
                            <StepCard
                                step={cluster.steps[0]}
                                t={t}
                            />
                        ) : (
                            <ClusterCard
                                steps={cluster.steps}
                                t={t}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
